import React, { useEffect, useRef, useState } from 'react';

// Inline "send code / enter code" verification for ONE channel (phone or
// email), rendered directly under that field on the single-page
// registration form — see registration.js's own header and the project
// plan for why this replaced the old post-submission
// bescom-phone-pending/bescom-email-pending gate. Purpose-built for
// Register.jsx's identity-prompt stage specifically, rather than folding
// this into the shared PromptStep.jsx (also used by AddEmail.jsx/
// AddPhone.jsx/Login.jsx for unrelated, already-working single-field
// flows) or reusing the full-page PhoneVerifyStep.jsx (also shared, with
// ClaimAccount.jsx/ForgotPassword.jsx/AddPhone.jsx, and built for a
// dedicated screen rather than a compact inline slot).
//
// Talks to phoneVerification.js's /send+/confirm (always defer:true here
// — no Authentik account exists yet) or the new, structurally identical
// emailVerification.js. `onVerified(value)` fires once the code is
// confirmed; the parent (Register.jsx) tracks verified-channel state and
// gates the Continue button on it.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const RESEND_COOLDOWN_SECONDS = 30;

const codeInp = {
  width: '140px',
  padding: '8px 10px',
  border: '1.5px solid rgba(211, 209, 199, 0.6)',
  borderRadius: '8px',
  fontSize: '16px',
  textAlign: 'center',
  letterSpacing: '3px',
  boxSizing: 'border-box',
  color: '#2c2c2a',
  background: 'rgba(255, 255, 255, 0.55)',
  outline: 'none',
};

const smallBtn = {
  padding: '8px 14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const smallBtnDisabled = { ...smallBtn, opacity: 0.6, cursor: 'not-allowed' };

const linkBtn = {
  background: 'none',
  border: 'none',
  fontSize: '12px',
  fontWeight: '500',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};

export default function InlineOtpField({ channel, value, username, onVerified, dark }) {
  const bodyColor = dark ? 'rgba(255, 255, 255, 0.75)' : '#666';
  const linkColor = dark ? '#8FD3FF' : '#0072B1';
  const errorColor = dark ? '#FF8A80' : '#B3261E';
  const okColor = dark ? '#7EE0A8' : '#1E7A3D';

  const [status, setStatus] = useState('idle'); // idle | sending | sent | verifying | verified
  const [verificationId, setVerificationId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Tracks which exact value was verified — if the person edits the field
  // afterward, that value no longer matches and verification must happen
  // again (prevents silently carrying a verified flag for a DIFFERENT
  // phone/email than the one actually being submitted).
  const verifiedValueRef = useRef(null);

  useEffect(() => {
    if (status === 'verified' && value !== verifiedValueRef.current) {
      setStatus('idle');
      setVerificationId(null);
      setCode('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function sendCode({ isResend } = {}) {
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${channel === 'phone' ? 'phone-verify' : 'email-verify'}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [channel]: value, username, resend: !!isResend }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not send verification code.');
        setStatus('idle');
        return;
      }
      setVerificationId(data.verificationId);
      setCode('');
      setStatus('sent');
      if (data.sent !== false) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (e) {
      setError('Could not reach the account service.');
      setStatus('idle');
    }
  }

  async function submitCode(e) {
    if (e) e.preventDefault();
    setStatus('verifying');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${channel === 'phone' ? 'phone-verify' : 'email-verify'}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          channel === 'phone'
            ? { verificationId, code, username, defer: true }
            : { verificationId, code, username }
        ),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        setStatus('sent');
        return;
      }
      verifiedValueRef.current = value;
      setStatus('verified');
      onVerified(channel === 'phone' ? data.phone : data.email);
    } catch (e) {
      setError('Could not reach the account service.');
      setStatus('sent');
    }
  }

  if (status === 'verified') {
    return (
      <p style={{ color: okColor, fontSize: '13px', margin: '4px 0 0', fontWeight: '500' }}>
        ✓ Verified
      </p>
    );
  }

  return (
    <div style={{ marginTop: '6px' }}>
      {status === 'idle' && (
        <button type="button" onClick={() => sendCode()} style={smallBtn}>
          {channel === 'phone' ? 'Send code to this number' : 'Send code to this email'}
        </button>
      )}

      {status === 'sending' && (
        <p style={{ color: bodyColor, fontSize: '13px', margin: 0 }}>Sending code…</p>
      )}

      {(status === 'sent' || status === 'verifying') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <input
            style={codeInp}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (code.length === 6 && status !== 'verifying') submitCode();
              }
            }}
            autoFocus
          />
          <button
            type="button"
            onClick={() => submitCode()}
            disabled={status === 'verifying' || code.length !== 6}
            style={(status === 'verifying' || code.length !== 6) ? smallBtnDisabled : smallBtn}
          >
            {status === 'verifying' ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={() => sendCode({ isResend: true })}
            disabled={resendCooldown > 0}
            style={{ ...linkBtn, color: linkColor, opacity: resendCooldown > 0 ? 0.6 : 1, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer' }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
          </button>
        </div>
      )}

      {error && (
        <p style={{ color: errorColor, fontSize: '13px', margin: '4px 0 0' }}>{error}</p>
      )}
    </div>
  );
}
